import React from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { ReactComponent as CloseRedIcon } from '../../assets/SVG/close-circlered.svg';

interface DiscardConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onDiscard: () => void;
  title?: string;
  message?: React.ReactNode;
  discardText?: string;
  keepEditingText?: string;
  showCloseButton?: boolean; // reserved for future use
}

const DiscardConfirmationModal: React.FC<DiscardConfirmationModalProps> = ({
  open,
  onClose,
  onDiscard,
  title,
  message,
  discardText,
  keepEditingText,
}) => {
  const { t } = useTranslation('general');

  const localizedTitle = title ?? t('DiscardChanges');
  const localizedMessage = message ?? t('DiscardChangesMessage');
  const localizedDiscard = discardText ?? t('Discard');
  const localizedKeepEditing = keepEditingText ?? t('KeepEditing');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          width: '90%',
          maxWidth: 434,
          minWidth: 300,
          p: 2,
        },
      }}
      aria-labelledby="discard-dialog-title"
      aria-describedby="discard-dialog-description"
    >
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          p: 0,
          pt: 1,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: '#FDEAEA',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CloseRedIcon style={{ width: 24, height: 24 }} />
        </Box>

        <Typography
          id="discard-dialog-title"
          variant="h6"
          sx={{ fontWeight: 700, fontSize: '1.25rem', textAlign: 'center' }}
        >
          {localizedTitle}
        </Typography>

        <Typography
          id="discard-dialog-description"
          sx={{ color: 'text.secondary', textAlign: 'center', fontSize: '0.9rem', lineHeight: 1.4 }}
        >
          {localizedMessage}
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          display: 'flex',
          gap: 1.5,
          p: 0,
          pt: 2,
        }}
      >
        <Button
          data-testid="discard-confirm-btn"
          variant="outlined"
          fullWidth
          onClick={() => {
            onDiscard();
            onClose();
          }}
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            py: 1.25,
            fontSize: '0.95rem',
            fontWeight: 600,
            bgcolor: '#F5F5F5',
            borderColor: '#E8E8E8',
            color: '#333',
            '&:hover': {
              bgcolor: '#EEEEEE',
              borderColor: '#D0D0D0',
            },
          }}
        >
          {localizedDiscard}
        </Button>

        <Button
          data-testid="discard-keep-editing-btn"
          variant="contained"
          fullWidth
          onClick={onClose}
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            py: 1.25,
            fontSize: '0.95rem',
            fontWeight: 600,
            bgcolor: '#3D3D3D',
            color: '#FFFFFF',
            '&:hover': {
              bgcolor: '#2D2D2D',
            },
            boxShadow: 'none',
          }}
        >
          {localizedKeepEditing}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiscardConfirmationModal;
